// Libs
import clsx from "clsx";
import React, { useCallback, useMemo, useRef } from "react";

// Utils
import { useFocus, useForkRef } from "../../core/hooks";
import { filterProps, patchFormEventValue } from "../../core/utils";

// Components
import type { FieldLabelProps } from "src/design-system/molecules/inputs/FieldLabel";
import type { Icons } from "../icons";
import {
    List,
    ListItem,
    ListItemActions,
    ListItemIcon,
    type ListItemProps,
    ListItemText,
    type ListProps,
} from "../lists";
import { Dropdown, type DropdownProps } from "../modals";
import { Checkbox } from "./Checkbox";
import { FieldHoc } from "./Field";
import { Input, type InputProps } from "./Input";
import { InputAdornment } from "./InputAdornment";
import { type FieldOption, getLabelFromOptions } from "./_utils";

/**
 * Props for CheckboxSelect and CheckboxSelectField.
 * This mirrors SelectProps in look-and-feel but supports multi-selection via checkboxes.
 */
export interface CheckboxSelectProps {
    /** Additional className for the component container */
    className?: string;
    /** If the input is visually inactive (read-only style) */
    inactive?: boolean;
    /** If the input is disabled */
    disabled?: boolean;
    /** Additional className for the dropdown */
    dropdownClassName?: string;
    /** Additional props passed to Dropdown */
    dropdownProps?: DropdownProps;
    /** If the input has background */
    hasBackground?: boolean;
    /** If the input has an error state */
    hasError?: boolean;
    /** If the input should take full width */
    hasFullWidth?: boolean;
    /** Icon to display at rest */
    icon?: Icons;
    /** Icon to display when focused/open */
    iconFocus?: Icons;
    /** Identifier */
    id?: string;
    /** Additional className for the inner Input */
    inputClassName?: string;
    /** Additional props for the inner Input */
    inputProps?: Partial<InputProps>;
    /** Label displayed above the input (via Field) */
    label?: string;
    /** Additional className for the label */
    labelClassName?: string;
    /** Additional props for the label component */
    labelProps?: FieldLabelProps;
    /** Additional className for the dropdown List */
    listClassName?: string;
    /** Additional className for each ListItem */
    listItemClassName?: string;
    /** Additional props for ListItem */
    listItemProps?: ListItemProps;
    /** Additional props for the List */
    listProps?: ListProps;
    /** Name of the control */
    name: string;
    /** Change handler: e.target.value contains the array of selected values */
    onChange: React.ChangeEventHandler<HTMLInputElement>;
    /** Blur handler */
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    /** Focus handler */
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    /** Options to render in the dropdown */
    options: FieldOption[];
    /** Placeholder text when no values are selected */
    placeholder?: string;
    /** If at least one selection is required */
    required?: boolean;
    /** Forced displayed value replacing the computed summary */
    displayValue?: string;
    /** Current selected values */
    value: (number | string | undefined)[];
}

/**
 * CheckboxSelect presents a Select-like input with a dropdown list of checkboxes allowing multi selection.
 */
export const CheckboxSelect = React.forwardRef<HTMLInputElement, CheckboxSelectProps>(
    function CheckboxSelect(
        {
            className,
            inactive = false,
            disabled = false,
            dropdownClassName: dropdownClassNameProp,
            dropdownProps,
            hasBackground = true,
            hasError = false,
            hasFullWidth = false,
            icon = "drop",
            iconFocus = "drop",
            id,
            inputClassName: inputClassNameProp,
            inputProps,
            label,
            labelClassName,
            labelProps,
            listClassName: listClassNameProp,
            listItemClassName: listItemClassNameProp,
            listItemProps,
            listProps,
            name,
            onChange,
            onBlur,
            onFocus,
            options,
            placeholder,
            required = false,
            displayValue,
            value,
            ...otherProps
        },
        ref
    ) {
        const selectClassName = clsx("ds-c-checkbox-select", className, {
            "ds-c-checkbox-select--full-width": hasFullWidth,
        });
        const inputClassName = clsx("ds-c-checkbox-select__input", inputClassNameProp);
        const dropdownClassName = clsx(
            "ds-c-checkbox-select__dropdown",
            dropdownClassNameProp,
            dropdownProps?.className
        );
        const listClassName = clsx("ds-c-checkbox-select__list", listClassNameProp);

        const inputContainerRef = useRef<HTMLInputElement>(null);
        const inputRef = useRef<HTMLInputElement>(null);
        const forkedRef = useForkRef(inputRef, ref);

        const { focus: open, setFocus: setOpen, _handleFocus } = useFocus(disabled, onFocus);

        const summaryLabel = useMemo(() => {
            if (!value?.length) {
                return placeholder ?? "";
            }

            const selectedLabels = value
                .map((v) => getLabelFromOptions(options, v))
                .filter(Boolean) as string[];
            return selectedLabels.join(" - ");
        }, [options, placeholder, value]);

        const _toggleValue = useCallback(
            (
                event: React.MouseEvent | React.ChangeEvent,
                toggledValue: string | number | undefined
            ) => {
                const exists = value?.includes(toggledValue);
                const newArray = exists
                    ? value.filter((v) => v !== toggledValue)
                    : [...(value ?? []), toggledValue];

                if (required && newArray.length === 0) {
                    return; // do not allow empty when required
                }
                const newEvent = patchFormEventValue(
                    event,
                    inputRef.current ?? (event.target as HTMLInputElement),
                    newArray
                );
                onChange?.(newEvent as unknown as React.ChangeEvent<HTMLInputElement>);
            },
            [onChange, required, value]
        );

        const endAdornment = useMemo(
            () => [
                <InputAdornment className="ds-c-checkbox-select__icon" key="focus">
                    {open ? iconFocus : icon}
                </InputAdornment>,
                ...(inputProps && Array.isArray(inputProps.endAdornment)
                    ? inputProps.endAdornment
                    : [inputProps?.endAdornment].filter(Boolean)),
            ],
            [icon, iconFocus, inputProps, open]
        );

        return (
            <div {...filterProps(otherProps)} className={selectClassName}>
                <Input
                    {...inputProps}
                    className={inputClassName}
                    containerRef={inputContainerRef}
                    disabled={disabled}
                    inactive={inactive}
                    endAdornment={endAdornment}
                    hasBackground={hasBackground}
                    hasError={hasError}
                    hasFullWidth={hasFullWidth}
                    id={id}
                    name={name}
                    label={label}
                    labelClassName={labelClassName}
                    labelProps={labelProps}
                    onClick={() => setOpen(!open)}
                    onBlur={onBlur}
                    onFocus={_handleFocus}
                    placeholder={placeholder}
                    readOnly
                    ref={forkedRef}
                    required={required}
                    type="text"
                    value={displayValue ?? summaryLabel}
                    InputComponent="div"
                />
                <Dropdown
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                    }}
                    {...dropdownProps}
                    anchorEl={inputContainerRef}
                    className={dropdownClassName}
                    hasFullWidth={dropdownProps?.hasFullWidth ?? false}
                    open={open}
                    inputRef={inputContainerRef}
                    onClose={() => setOpen(false)}
                >
                    <List {...listProps} className={listClassName}>
                        {options?.map(
                            ({ value: cValue, disabled = false, icon, label, secondary }) => {
                                const liClassName = clsx(
                                    "ds-c-checkbox-select__list-item",
                                    listItemClassNameProp
                                );
                                return (
                                    <ListItem
                                        {...listItemProps}
                                        className={liClassName}
                                        condensed
                                        disabled={disabled}
                                        key={String(cValue)}
                                        onClick={(event) => _toggleValue(event, cValue)}
                                    >
                                        <ListItemActions>
                                            <Checkbox
                                                name={name}
                                                value={cValue as any}
                                                checked={value?.includes(cValue)}
                                                onChange={(event) =>
                                                    _toggleValue(
                                                        event,
                                                        (event.target as HTMLInputElement)
                                                            .value as any
                                                    )
                                                }
                                            />
                                        </ListItemActions>
                                        {icon && <ListItemIcon icon={icon} />}
                                        <ListItemText primary={label} secondary={secondary} />
                                    </ListItem>
                                );
                            }
                        )}
                    </List>
                </Dropdown>
            </div>
        );
    }
);

export const CheckboxSelectField = FieldHoc<CheckboxSelectProps>(CheckboxSelect, "CheckboxSelect", {
    innerLabel: true,
});
